// Generic searchable picker used for banks, branches, countries and billers.
import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

export interface PickerRow {
  key: string;
  title: string;
  subtitle?: string;
  disabled?: boolean;
  disabledNote?: string;
}

interface PickerModalProps {
  show: boolean;
  title: string;
  searchHint: string;
  rows: PickerRow[];
  emptyText: string;
  onSelect: (row: PickerRow) => void;
  onClose: () => void;
}

const PickerModal = ({ show, title, searchHint, rows, emptyText, onSelect, onClose }: PickerModalProps) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (show) setQuery('');
  }, [show]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) || (r.subtitle ?? '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  return (
    <Modal show={show} onHide={onClose} centered scrollable className="nova-picker-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="search"
          className="form-control mb-3"
          placeholder={searchHint}
          aria-label={searchHint}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {filtered.length === 0 && <p className="nova-picker-empty">{emptyText}</p>}
        <div className="nova-picker-rows">
          {filtered.map((row) => (
            <button
              type="button"
              key={row.key}
              className="nova-picker-row"
              disabled={row.disabled}
              onClick={() => onSelect(row)}
            >
              <span className="nova-picker-row-title">{row.title}</span>
              {row.subtitle && <span className="nova-picker-row-sub">{row.subtitle}</span>}
              {row.disabled && row.disabledNote && (
                <span className="nova-picker-row-note">{row.disabledNote}</span>
              )}
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PickerModal;
